import React, { useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import Cropper from 'react-easy-crop';
import { X, Check, RotateCw } from 'lucide-react';

/**
 * 4:5 portrait image cropper modal used by ProductShowcaseModal before upload.
 * 4:5 matches the Merch Store product-card image area shown to attendees, so
 * cropped product images render edge-to-edge in the card without letterboxing.
 *
 * Props:
 *   - file: the File object the user picked. The modal reads it as a data URL,
 *           lets the user pan/zoom inside a square crop frame, and on confirm
 *           returns a cropped Blob (JPEG, ~0.92 quality) via onConfirm.
 *   - onConfirm(blob, originalFile): called with the cropped Blob.
 *   - onCancel(): called when the user dismisses without cropping.
 *
 * Why a Blob and not a data URL: uploadService.uploadFile expects a File-like
 * object and Blobs work fine — the caller wraps it back into a File so we
 * preserve the original filename.
 */
export default function ImageCropperModal({ file, onConfirm, onCancel }) {
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [processing, setProcessing] = useState(false);

  // Read the picked file into a data URL whenever it changes, and reset all
  // crop/zoom/rotation state so the previous file's coordinates don't bleed
  // into the next one. This is critical when the parent processes a queue of
  // multiple files through the same modal instance — without these resets,
  // file2 would inherit file1's crop rectangle and produce a blank/garbage
  // Blob if the user just hit Apply without re-cropping.
  React.useEffect(() => {
    if (!file) return;
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCroppedAreaPixels(null);
    const reader = new FileReader();
    reader.onload = () => setImageSrc(reader.result);
    reader.readAsDataURL(file);
  }, [file]);

  const onCropComplete = useCallback((_, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleConfirm = async () => {
    // Both must be truthy AND derived from the *current* file. The state
    // resets above ensure croppedAreaPixels is null until react-easy-crop
    // emits its first onCropComplete for the freshly-loaded image.
    if (!imageSrc || !croppedAreaPixels) return;
    setProcessing(true);
    try {
      const blob = await getCroppedBlob(imageSrc, croppedAreaPixels, rotation);
      onConfirm(blob, file);
    } catch (err) {
      console.error('[ImageCropper] Failed to crop image:', err);
      // Fall back to the original file so the upload still goes through.
      onConfirm(file, file);
    } finally {
      setProcessing(false);
    }
  };

  if (!file) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      style={{ zIndex: 100000 }}
      onClick={onCancel}
    >
      <div
        className="bg-[#0a0a1a] border border-white/10 rounded-2xl w-full max-w-lg flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h3 className="text-white text-base font-semibold">Crop Image</h3>
          <button
            onClick={onCancel}
            className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
            aria-label="Cancel"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Crop area */}
        <div className="relative w-full h-[60vh] max-h-[420px] bg-black">
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={4 / 5}
              cropShape="rect"
              showGrid={true}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onRotationChange={setRotation}
              onCropComplete={onCropComplete}
              objectFit="contain"
            />
          )}
        </div>

        {/* Controls */}
        <div className="p-4 space-y-3 border-t border-white/10">
          <div>
            <label className="text-xs text-gray-400 flex items-center justify-between mb-1">
              <span>Zoom</span>
              <span className="tabular-nums">{zoom.toFixed(2)}x</span>
            </label>
            <input
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="w-full accent-purple-500"
            />
          </div>

          <button
            type="button"
            onClick={() => setRotation((r) => (r + 90) % 360)}
            className="text-xs text-gray-300 hover:text-white flex items-center gap-1.5 px-2 py-1 rounded hover:bg-white/5 transition-colors"
          >
            <RotateCw className="w-3.5 h-3.5" />
            Rotate 90°
          </button>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/10 flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 border border-white/20 text-white rounded-lg text-sm font-medium hover:bg-white/10 transition-colors"
            disabled={processing}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={processing || !croppedAreaPixels}
            className="px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg text-sm font-semibold flex items-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {processing ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Check className="w-4 h-4" />
            )}
            {processing ? 'Processing...' : 'Apply Crop'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ---------------------------------------------------------------------------
// Canvas helpers — render the selected crop region (with optional rotation)
// to a canvas and return a JPEG Blob. JPEG keeps the file small for product
// thumbnails; quality 0.92 is visually lossless for this use case.
// ---------------------------------------------------------------------------

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

async function getCroppedBlob(imageSrc, pixelCrop, rotation = 0) {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');

  // For rotation, draw the rotated image into a larger intermediate canvas
  // first, then take the crop from that. For zero rotation we can crop
  // directly out of the source image — faster and avoids interpolation loss.
  if (rotation === 0) {
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;
    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );
  } else {
    const rad = (rotation * Math.PI) / 180;
    const sin = Math.abs(Math.sin(rad));
    const cos = Math.abs(Math.cos(rad));
    const bBoxWidth = image.width * cos + image.height * sin;
    const bBoxHeight = image.width * sin + image.height * cos;

    const intermediate = document.createElement('canvas');
    intermediate.width = bBoxWidth;
    intermediate.height = bBoxHeight;
    const iCtx = intermediate.getContext('2d');
    iCtx.translate(bBoxWidth / 2, bBoxHeight / 2);
    iCtx.rotate(rad);
    iCtx.drawImage(image, -image.width / 2, -image.height / 2);

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;
    ctx.drawImage(
      intermediate,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );
  }

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) reject(new Error('Canvas toBlob returned null'));
        else resolve(blob);
      },
      'image/jpeg',
      0.92
    );
  });
}
